import React, { useState, useEffect } from "react";
import {
  AppBar, Box, Button, Toolbar, IconButton, Menu, MenuItem, useMediaQuery, useTheme,
  Divider, Typography, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  ShoppingCart,
  ListAlt,
  TrendingUp,
  Grade,
  Campaign,
  HelpOutline,
  Insights,
} from '@mui/icons-material';
import AddDeviceDialog from '../AddDeviceDialog';
import GradeCriteriaGuideDialog from '../common/GradeCriteriaGuideDialog';
import FaqDrawer from './FaqDrawer';
import { useChannelPath } from '../../../contexts/ChannelContext';

const KAKAO_CHANNEL_URL = 'https://pf.kakao.com/_QXtCxj/chat';

const AppBarSection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [hamburgerAnchor, setHamburgerAnchor] = useState<null | HTMLElement>(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [priceQuoteDialogOpen, setPriceQuoteDialogOpen] = useState(false);
  const [gradeGuideOpen, setGradeGuideOpen] = useState(false);
  const [faqDrawerOpen, setFaqDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const channelPath = useChannelPath();

  // 스크롤 감지
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleHamburgerClick = (event: React.MouseEvent<HTMLElement>) => {
    setHamburgerAnchor(event.currentTarget);
  };
  const handleHamburgerClose = () => setHamburgerAnchor(null);

  const goTo = (href: string) => {
    window.location.href = href;
    setHamburgerAnchor(null);
    setMobileDrawerOpen(false);
  };

  const openPriceQuoteDialog = () => {
    setHamburgerAnchor(null);
    setMobileDrawerOpen(false);
    setPriceQuoteDialogOpen(true);
  };

  const openGradeGuide = () => {
    setHamburgerAnchor(null);
    setMobileDrawerOpen(false);
    setGradeGuideOpen(true);
  };

  const openFaq = () => {
    setHamburgerAnchor(null);
    setMobileDrawerOpen(false);
    setFaqDrawerOpen(true);
  };

  const openKakaoChat = () => {
    setHamburgerAnchor(null);
    setMobileDrawerOpen(false);
    window.open(KAKAO_CHANNEL_URL, '_blank', 'noopener,noreferrer');
  };

  // 메뉴 항목 정의
  const mainMenuItems = [
    { label: '매입 신청', onClick: () => goTo(channelPath('/apply')), icon: <ShoppingCart sx={{ fontSize: 20 }} /> },
    { label: '내 신청 내역', onClick: () => goTo(channelPath('/my-list')), icon: <ListAlt sx={{ fontSize: 20 }} /> },
    { label: '시세 조회', onClick: openPriceQuoteDialog, icon: <TrendingUp sx={{ fontSize: 20 }} /> },
    { label: '등급 안내', onClick: openGradeGuide, icon: <Grade sx={{ fontSize: 20 }} /> },
  ];

  const subMenuItems = [
    { label: '공지사항', onClick: () => goTo(channelPath('/notices')), icon: <Campaign sx={{ fontSize: 20 }} /> },
    { label: 'FAQ', onClick: () => goTo(channelPath('/faq')), icon: <HelpOutline sx={{ fontSize: 20 }} /> },
    { label: '데이터솔루션', onClick: () => goTo('/trust'), icon: <Insights sx={{ fontSize: 20 }} /> },
  ];

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: scrolled ? 'rgba(255,255,255,0.85)' : '#fff',
          backdropFilter: scrolled ? 'blur(16px) saturate(180%)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(0,0,0,0.06)' : '1px solid #f0f0f0',
          boxShadow: scrolled ? '0 4px 20px rgba(0,0,0,0.06)' : 'none',
          transition: 'all 0.3s ease',
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            maxWidth: { xs: '100%', md: '50%' },
            width: '100%',
            mx: 'auto',
            px: { xs: 2, md: 0 },
            py: 0.5,
            minHeight: { xs: 52, md: 56 },
          }}
        >
          {/* 좌측: 로고 */}
          <Box
            component="a"
            href={channelPath('/')}
            sx={{
              display: "flex", alignItems: "center", textDecoration: "none", flexShrink: 0,
              transition: 'opacity 0.2s',
              '&:hover': { opacity: 0.8 },
            }}
          >
            <Box
              component="img"
              src="/assets/dwt_logo.png"
              alt="다원트레이드 로고"
              sx={{ height: { xs: 26, md: 30 }, width: "auto" }}
            />
          </Box>

          {/* 데스크탑: 중앙 메뉴 */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {mainMenuItems.map((item) => (
                <Button
                  key={item.label}
                  onClick={item.onClick}
                  sx={{
                    fontWeight: 600,
                    textTransform: 'none',
                    color: '#475569',
                    fontSize: '0.85rem',
                    px: 2,
                    py: 0.8,
                    minWidth: 'auto',
                    borderRadius: 2,
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: 'rgba(59, 130, 246, 0.04)',
                      color: '#1d4ed8',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 4,
                      left: '50%',
                      transform: 'translateX(-50%) scaleX(0)',
                      width: '60%',
                      height: 2,
                      borderRadius: 1,
                      background: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
                      transition: 'transform 0.25s ease',
                    },
                    '&:hover::after': {
                      transform: 'translateX(-50%) scaleX(1)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {/* 우측 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* 데스크탑: 햄버거 */}
            {!isMobile && (
              <>
                <IconButton
                  onMouseEnter={handleHamburgerClick}
                  onClick={handleHamburgerClick}
                  size="small"
                  sx={{
                    color: '#64748b',
                    width: 36, height: 36,
                    border: '1px solid #e5e7eb',
                    borderRadius: 2,
                    '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
                    transition: 'all 0.2s',
                  }}
                >
                  <MenuIcon sx={{ fontSize: 20 }} />
                </IconButton>

                <Menu
                  anchorEl={hamburgerAnchor}
                  open={Boolean(hamburgerAnchor)}
                  onClose={handleHamburgerClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  slotProps={{
                    paper: {
                      onMouseLeave: handleHamburgerClose,
                      sx: {
                        mt: 1, minWidth: 200, borderRadius: 2.5,
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                      },
                    },
                  }}
                  MenuListProps={{ onMouseLeave: handleHamburgerClose }}
                >
                  {subMenuItems.map((item) => (
                    <MenuItem
                      key={item.label}
                      onClick={item.onClick}
                      sx={{
                        py: 1.2, px: 2,
                        gap: 1.5,
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        color: '#475569',
                        '&:hover': { bgcolor: '#f8fafc', color: '#1d4ed8' },
                      }}
                    >
                      <Box sx={{ color: '#94a3b8' }}>{item.icon}</Box>
                      {item.label}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}

            {/* 모바일: 햄버거 */}
            {isMobile && (
              <IconButton
                onClick={() => setMobileDrawerOpen(true)}
                size="small"
                sx={{
                  color: '#475569',
                  width: 36, height: 36,
                }}
              >
                <MenuIcon sx={{ fontSize: 24 }} />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* 모바일 풀스크린 드로어 */}
      <Drawer
        anchor="right"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: '80%',
            maxWidth: 320,
            bgcolor: '#fff',
          },
        }}
      >
        {/* 드로어 헤더 */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2.5, py: 2,
          borderBottom: '1px solid #f0f0f0',
        }}>
          <Box
            component="img"
            src="/assets/dwt_logo.png"
            alt="다원트레이드"
            sx={{ height: 24 }}
          />
          <IconButton onClick={() => setMobileDrawerOpen(false)} size="small" sx={{ color: '#94a3b8' }}>
            <CloseIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </Box>

        {/* 메인 메뉴 */}
        <Box sx={{ px: 1, pt: 1.5 }}>
          <Typography sx={{ px: 2, fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', mb: 0.5 }}>
            MENU
          </Typography>
          <List disablePadding>
            {mainMenuItems.map((item) => (
              <ListItemButton
                key={item.label}
                onClick={item.onClick}
                sx={{
                  borderRadius: 2, mx: 0.5, mb: 0.3,
                  py: 1.3,
                  '&:hover': { bgcolor: '#f8fafc' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: '#64748b' }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem', color: '#334155' }} />
              </ListItemButton>
            ))}
          </List>
        </Box>

        <Divider sx={{ mx: 2.5, my: 1.5 }} />

        {/* 서브 메뉴 */}
        <Box sx={{ px: 1 }}>
          <Typography sx={{ px: 2, fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', mb: 0.5 }}>
            SUPPORT
          </Typography>
          <List disablePadding>
            {subMenuItems.map((item) => (
              <ListItemButton
                key={item.label}
                onClick={item.onClick}
                sx={{
                  borderRadius: 2, mx: 0.5, mb: 0.3,
                  py: 1.2,
                  '&:hover': { bgcolor: '#f8fafc' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: '#94a3b8' }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 500, fontSize: '0.85rem', color: '#64748b' }} />
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* 하단 */}
        <Box sx={{ mt: 'auto', p: 2.5, borderTop: '1px solid #f0f0f0' }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#cbd5e1', textAlign: 'center' }}>
            &copy; {new Date().getFullYear()} 다원트레이드
          </Typography>
        </Box>
      </Drawer>

      <AddDeviceDialog
        open={priceQuoteDialogOpen}
        onClose={() => setPriceQuoteDialogOpen(false)}
        mode="quote"
      />

      <GradeCriteriaGuideDialog
        open={gradeGuideOpen}
        onClose={() => setGradeGuideOpen(false)}
      />

      <FaqDrawer open={faqDrawerOpen} onClose={() => setFaqDrawerOpen(false)} />
    </>
  );
};

export default AppBarSection;
